import { FunctionComponent, ReactNode, useRef, useState } from "react";
import TextFieldAtom from "../../../../atoms/textfield";
import { LabelPairedChevronDownSmBoldIcon } from "@deriv/quill-icons";
import { useClickOutside } from "../../../../hooks/useClickOutside";
import {
  DropdownDataType,
  DropdownItemType,
} from "../../../../atoms/dropdown/container";
import WheelpickerContainerAtom from "../../../../atoms/dropdown/wheelpicker";

import { usePortalClick } from "../../../../hooks/usePortalClick";
import { ActionSheetType } from "../../../action-sheet";

export type GenericWheelpickerType = {
  className?: string;
  label?: string;
  placeholder?: string;
  message?: string;

  /** Variant props */
  indicator?: "neutral" | "fail" | "success";
  state?: "default" | "disabled";
  type?: "outline" | "fill";
  size?: "lg" | "md" | "sm";
  textAlignment?: "left" | "center";

  /** Custom  input props */
  itemSize?: "md" | "sm";
  itemWidth?: "fill" | "fit-content";
  iconLeft?: ReactNode;
  labelLeft?: string;
  isRequired?: boolean;
  optionalText?: string;
  containerRef?: React.RefObject<HTMLDivElement>;
  customValue?: string;
  onChange?: (item: DropdownItemType | DropdownItemType[]) => void;
  onClick?: () => void;
  variant?: "labelled" | "labelless";
  actionsheet?: ActionSheetType;

  /**Custom Dropdown props */
  data?: DropdownDataType[];
  selected?: DropdownItemType[];
};

const GenericWheelpicker: FunctionComponent<GenericWheelpickerType> = ({
  className,
  label = "label",
  placeholder = "Select an option",
  message,
  indicator,
  state,
  type,
  size = "md",
  itemSize = "sm",
  itemWidth = "fill",
  textAlignment = "left",
  iconLeft,
  labelLeft,
  isRequired,
  optionalText,
  containerRef,
  selected: internalSelected,
  actionsheet,
  customValue,
  onChange,
  onClick,
  data = [],
  variant = "labelled",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const defaultRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = containerRef || defaultRef;
  const [selected, setSelected] = useState<DropdownItemType[] | undefined>(
    internalSelected
  );
  const handleClick = (e: React.MouseEvent) => {
    const { isPortalClick } = usePortalClick(
      dropdownRef as React.RefObject<HTMLElement>
    );

    if (isPortalClick(e) || state === "disabled") {
      return;
    }

    const target = e.target as HTMLElement;
    if (target.closest('.wheelpicker-container')) {
      setIsOpen(true);
    } else {
      setIsOpen(!isOpen);
    }
    onClick?.();
  };

  useClickOutside(
    dropdownRef,
    () => {
      setIsOpen(false);
    },
    ["wheelpicker-container"]
  );

  return (
    <TextFieldAtom
      ref={inputRef}
      containerRef={dropdownRef as React.RefObject<HTMLDivElement>}
      label={label}
      placeholder={placeholder}
      message={message}
      className={`quill-ui-next-pointer ${className}`}
      allowTyping={false}
      onClick={(e) => handleClick(e)}
      iconRight={
        <LabelPairedChevronDownSmBoldIcon
          className={`quill-ui-next-rotatable ${
            isOpen && "quill-ui-next-rotate"
          }`}
        />
      }
      indicator={indicator}
      state={state}
      type={type}
      size={size}
      textAlignment={textAlignment}
      iconLeft={iconLeft}
      labelLeft={labelLeft}
      isRequired={isRequired}
      optionalText={optionalText}
      variant={variant}
      value={customValue || selected?.map((item) => item.label).join(" ")}
      customContent={
        <WheelpickerContainerAtom
          width="100%"
          offsetTop={4}
          textAlignment={textAlignment}
          parent={dropdownRef}
          isOpen={isOpen}
          itemSize={itemSize}
          itemWidth={itemWidth}
          data={data}
          containerSize="md"
          selected={selected}
          actionsheet={{
            onClose: () => setIsOpen(false),
            ...actionsheet,
          }}
          onChange={(e: DropdownItemType | DropdownItemType[]) => {
            onChange?.(e);
            setSelected(e as DropdownItemType[]);
            inputRef.current?.focus();
          }}
        />
      }
    />
  );
};

GenericWheelpicker.displayName = "GenericWheelpicker";

export default GenericWheelpicker;
